import { useCallback, useId, useState, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { QcFieldInput } from '@/features/workflows';

import { useSubmitQc } from '../hooks/use-workflow-mutations';
import {
  initialQcAnswers,
  missingRequired,
  toQcAnswersBody,
  type QcAnswers,
} from '../schemas/qc-answers';
import { needsDelayReason } from '../schemas/stage-rules';

import { ActionDialog } from './action-dialog';
import { QcFileAnswer } from './qc-file-answer';
import type { StageTarget } from './stage-target';

/**
 * The stage's checklist as it was when the workflow started, prefilled from the order (or from
 * the last submission when QC is done again after rework). Required questions and file sizes
 * are checked here; the backend repeats both and its field errors land on the question.
 */
export function QcFormDialog({
  target,
  onOpenChange,
}: {
  target: StageTarget;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('production');
  const format = useFormat();
  const message = useErrorMessage();
  const baseId = useId();
  const submitQc = useSubmitQc(target.storeId, target.orderId);
  const fields = target.stage.qcFields;
  const [answers, setAnswers] = useState<QcAnswers>(() => initialQcAnswers(fields, target.run));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fileProblems, setFileProblems] = useState<Record<string, string | null>>({});
  const [delayReason, setDelayReason] = useState('');
  const [delayError, setDelayError] = useState<string | null>(null);
  const [rootError, setRootError] = useState<string | null>(null);
  const [forceDelay, setForceDelay] = useState(false);
  const running = target.run.status === 'in_progress';
  const showDelay = running && (needsDelayReason(target.run) || forceDelay);
  const prefill = target.run.prefill;

  const setAnswer = (key: string, value: QcAnswers[string]) => {
    setAnswers((current) => ({ ...current, [key]: value }));
    setErrors(({ [key]: _gone, ...rest }) => rest);
  };
  const reportFile = useCallback(
    (key: string, problem: string | null) =>
      setFileProblems((current) =>
        current[key] === problem ? current : { ...current, [key]: problem },
      ),
    [],
  );

  const submit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    setRootError(null);
    const next: Record<string, string> = {};
    for (const key of missingRequired(fields, answers)) next[key] = t('qc.required');
    for (const [key, problem] of Object.entries(fileProblems)) if (problem) next[key] = problem;
    const needDelay = showDelay && delayReason.trim() === '';
    setErrors(next);
    setDelayError(needDelay ? t('validation.delayReason') : null);
    if (Object.keys(next).length > 0 || needDelay) return;
    try {
      await submitQc.mutateAsync({
        key: target.key,
        body: {
          answers: toQcAnswersBody(fields, answers),
          ...(showDelay ? { delayReason: delayReason.trim() } : {}),
        },
      });
      onOpenChange(false);
    } catch (error) {
      if (!isApiError(error)) {
        setRootError(message(error));
        return;
      }
      if (error.code === 'DELAY_REASON_REQUIRED') setForceDelay(true);
      const fieldErrors: Record<string, string> = {};
      let placed = false;
      for (const detail of error.details) {
        const answer = /^body\.answers\.(.+)$/.exec(detail.field)?.[1];
        if (answer && fields.some((f) => f.key === answer)) {
          fieldErrors[answer] = detail.message;
          placed = true;
        } else if (detail.field === 'body.delayReason') {
          setDelayError(detail.message);
          placed = true;
        }
      }
      setErrors(fieldErrors);
      if (!placed) setRootError(message(error));
    }
  };

  return (
    <ActionDialog
      open
      wide
      onOpenChange={onOpenChange}
      title={t('qc.title', { name: target.name })}
      description={t('qc.description')}
      rootError={rootError ?? undefined}
      isPending={submitQc.isPending}
      submitLabel={t('qc.submit')}
      submittingLabel={t('qc.submitting')}
      onSubmit={(event) => void submit(event)}
    >
      {fields.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('qc.empty')}</p>
      ) : null}
      <ol className="flex flex-col gap-5" aria-label={t('qc.title', { name: target.name })}>
        {fields.map((field) => {
          const hintId = `${baseId}-${field.key}-hint`;
          const errorId = `${baseId}-${field.key}-error`;
          const error = errors[field.key];
          const prefilled = prefill[field.key];
          return (
            <li key={field.key} className="flex flex-col gap-2">
              <p className="text-sm font-medium" id={`${baseId}-${field.key}-label`}>
                {field.label}
                {field.required ? (
                  <span className="text-destructive ml-1" aria-hidden>
                    *
                  </span>
                ) : null}
              </p>
              {prefilled ? (
                <p id={hintId} className="text-muted-foreground text-xs">
                  {t('qc.prefilled')}: {prefilled}
                </p>
              ) : null}
              <QcFieldInput
                field={field}
                value={answers[field.key] ?? null}
                onChange={(value) => setAnswer(field.key, value)}
                invalid={Boolean(error)}
                describedBy={
                  [prefilled ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') ||
                  undefined
                }
                fileSlot={
                  <QcFileAnswerSlot
                    fieldKey={field.key}
                    label={field.label}
                    value={
                      typeof answers[field.key] === 'string' ? (answers[field.key] as string) : null
                    }
                    onChange={(mediaId) => setAnswer(field.key, mediaId)}
                    onValidity={reportFile}
                  />
                }
              />
              {error ? (
                <p id={errorId} role="alert" className="text-destructive text-sm">
                  {error}
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>
      {showDelay ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor={`${baseId}-delay`}>{t('complete.delayReason')}</Label>
          <Textarea
            id={`${baseId}-delay`}
            rows={3}
            maxLength={1000}
            value={delayReason}
            aria-invalid={delayError ? true : undefined}
            onChange={(e) => {
              setDelayReason(e.target.value);
              setDelayError(null);
            }}
          />
          <p className="text-muted-foreground text-sm">
            {target.run.dueAt
              ? t('complete.delayReasonHint', { due: format.dateTime(target.run.dueAt) })
              : t('complete.delayReasonHintNoDue')}
          </p>
          {delayError ? (
            <p role="alert" className="text-destructive text-sm">
              {delayError}
            </p>
          ) : null}
        </div>
      ) : null}
    </ActionDialog>
  );
}

function QcFileAnswerSlot({
  fieldKey,
  onValidity,
  ...rest
}: {
  fieldKey: string;
  label: string;
  value: string | null;
  onChange: (mediaId: string | null) => void;
  onValidity: (key: string, problem: string | null) => void;
}) {
  const report = useCallback(
    (problem: string | null) => onValidity(fieldKey, problem),
    [fieldKey, onValidity],
  );
  return <QcFileAnswer {...rest} onValidity={report} />;
}
